import React, { useEffect, useState } from "react";
import { Wallet as WalletIcon, Plus, Loader, ExternalLink, ArrowDownCircle, ArrowUpCircle, RefreshCw, Smartphone, ShieldCheck } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

/**
 * Funds — top-up + activity ledger for the viewer spending wallet.
 *
 * Distinct from /wallet (which is the creator cashout/giftcard surface).
 * This page is the TV-friendly + Watch.jsx-friendly source of funds for
 * tips and PPV unlocks. Stripe Checkout deposits credit a `wallet_balance_usd`
 * field on the user; tips and PPV unlocks debit it instantly with no
 * Stripe round-trip.
 */
export default function Funds() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(0); // amount currently being deposited
  const [custom, setCustom] = useState("");
  const [phonePay, setPhonePay] = useState(null); // { url, amount } when user picks "Pay on phone"
  const [polling, setPolling] = useState(false);

  const refresh = async () => {
    try { const r = await api.get("/funds/me"); setData(r.data); }
    catch { setData({ balance_usd: 0, points_balance: 0, preset_amounts: [5, 10, 25, 50, 100], recent_transactions: [], recent_deposits: [] }); }
  };

  useEffect(() => {
    if (!user) { nav("/login?next=/funds"); return; }
    refresh();
  }, [user, nav]);

  // Stripe success callback — deposit just landed; refresh balance.
  useEffect(() => {
    if (params.get("deposit") === "success") {
      toast.success("Deposit confirmed — checking balance…");
      let n = 0;
      setPolling(true);
      const t = setInterval(async () => {
        await refresh();
        if (++n >= 5) { clearInterval(t); setPolling(false); }
      }, 2000);
      return () => clearInterval(t);
    }
    if (params.get("deposit") === "cancel") {
      toast.error("Deposit cancelled");
    }
  }, [params]);

  // While "Pay on phone" QR is up, poll the deposit status until paid.
  useEffect(() => {
    if (!phonePay) return;
    const t = setInterval(refresh, 4000);
    return () => clearInterval(t);
  }, [phonePay]);

  const startDeposit = async (amount, mode = "stripe") => {
    setBusy(amount);
    try {
      const { data: r } = await api.post("/funds/deposit/checkout", {
        amount: Number(amount),
        origin_url: window.location.origin,
      });
      if (mode === "phone") {
        setPhonePay({ url: r.qr_url || r.url, amount: r.amount_usd });
      } else {
        const w = window.open(r.url, "_blank", "noopener,noreferrer");
        if (!w) window.location.href = r.url;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start deposit");
    } finally {
      setBusy(0);
    }
  };

  if (!data) return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <Loader className="w-8 h-8 text-tv-accent animate-spin" />
    </div>
  );

  const presets = data.preset_amounts || [5, 10, 25, 50, 100];

  return (
    <div data-testid="funds-page" className="pt-32 px-4 sm:px-8 md:px-12 pb-20 max-w-5xl mx-auto">
      <header className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-tv-accent font-bold inline-flex items-center gap-2">
            <WalletIcon className="w-4 h-4" /> Spending wallet
          </div>
          <h1 data-testid="funds-balance" className="font-display font-black text-4xl sm:text-5xl text-white mt-2 tracking-tight">${Number(data.balance_usd || 0).toFixed(2)}</h1>
          <div className="text-white/55 text-sm mt-1">
            {data.points_balance > 0 && <>Points: <strong className="text-white">{data.points_balance}</strong> · </>}
            Top up to instantly tip creators or unlock PPV without checkout redirects.
          </div>
        </div>
        <button
          data-testid="funds-refresh"
          onClick={refresh}
          className="inline-flex items-center gap-2 bg-white/8 hover:bg-white/15 text-white font-bold text-sm px-4 py-2 rounded-full"
        >
          <RefreshCw className={`w-4 h-4 ${polling ? "animate-spin" : ""}`} /> Refresh
        </button>
      </header>

      <section data-testid="funds-deposit-section" className="bg-white/5 border border-white/10 rounded-2xl p-5 sm:p-6">
        <h3 className="font-display font-bold text-xl text-white">Add funds</h3>
        <p className="text-white/55 text-sm mt-1">Pick a preset or enter a custom amount. Pays via Stripe — secure card / Apple Pay / Google Pay.</p>
        <div className="mt-4 flex flex-wrap gap-2">
          {presets.map((amt) => (
            <button
              key={amt}
              data-testid={`funds-deposit-${amt}`}
              disabled={busy === amt}
              onClick={() => startDeposit(amt)}
              className="inline-flex items-center gap-1 bg-tv-accent hover:bg-yellow-300 text-black font-display font-black text-base px-5 py-2.5 rounded-full disabled:opacity-60"
            >
              {busy === amt ? <Loader className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              ${amt}
            </button>
          ))}
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <input
            data-testid="funds-deposit-custom"
            type="number"
            min="1"
            max="500"
            step="1"
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
            placeholder="Custom amount"
            className="bg-white/5 border border-white/15 rounded-lg px-4 py-2.5 text-white outline-none focus:border-tv-accent w-44"
          />
          <button
            data-testid="funds-deposit-custom-btn"
            disabled={!custom || Number(custom) < 1 || busy > 0}
            onClick={() => startDeposit(Number(custom))}
            className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-bold px-4 py-2.5 rounded-full disabled:opacity-60"
          >
            <Plus className="w-4 h-4" /> Add
          </button>
          <button
            data-testid="funds-deposit-phone-btn"
            disabled={busy > 0}
            onClick={() => startDeposit(Number(custom) > 0 ? Number(custom) : 25, "phone")}
            className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-bold px-4 py-2.5 rounded-full disabled:opacity-60"
            title="Pay from your phone — useful on TV (defaults to $25 if no amount entered)"
          >
            <Smartphone className="w-4 h-4" /> Pay on phone
          </button>
        </div>
        <div className="mt-3 inline-flex items-center gap-1.5 text-[11px] text-white/45">
          <ShieldCheck className="w-3 h-3" /> Stripe-secured · Funds spend instantly on tips & PPV
        </div>
      </section>

      {phonePay && (
        <PhonePayQr
          url={phonePay.url}
          amount={phonePay.amount}
          onClose={() => setPhonePay(null)}
        />
      )}

      <section data-testid="funds-activity" className="mt-8">
        <h3 className="font-display font-bold text-xl text-white mb-3">Recent activity</h3>
        <div className="bg-white/5 border border-white/10 rounded-2xl divide-y divide-white/10">
          {(data.recent_transactions || []).length === 0 ? (
            <div className="p-6 text-white/55 text-sm text-center">No activity yet — your tips, PPV unlocks and deposits will show up here.</div>
          ) : (
            data.recent_transactions.map((tx) => <ActivityRow key={tx.id} tx={tx} />)
          )}
        </div>
      </section>
    </div>
  );
}

function ActivityRow({ tx }) {
  const credit = tx.amount_usd > 0;
  const Icon = credit ? ArrowDownCircle : ArrowUpCircle;
  const KIND_LABEL = {
    deposit: "Deposit",
    tip_spent: "Tip sent",
    ppv_spent: "PPV unlock",
    refund: "Refund",
    adjust: "Adjustment",
  };
  return (
    <div data-testid={`funds-tx-${tx.id}`} className="flex items-center gap-3 px-4 py-3">
      <Icon className={`w-5 h-5 ${credit ? "text-emerald-400" : "text-rose-400"}`} />
      <div className="flex-1 min-w-0">
        <div className="text-white font-bold text-sm">{KIND_LABEL[tx.kind] || tx.kind}</div>
        <div className="text-white/50 text-xs truncate">{tx.note || tx.ref_id || ""}</div>
      </div>
      <div className="text-right shrink-0">
        <div className={`font-display font-black ${credit ? "text-emerald-300" : "text-white"}`}>
          {credit ? "+" : ""}${Math.abs(tx.amount_usd).toFixed(2)}
        </div>
        <div className="text-white/40 text-[10px]">{new Date(tx.created_at).toLocaleString()}</div>
      </div>
    </div>
  );
}

function PhonePayQr({ url, amount, onClose }) {
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(url)}`;
  return (
    <div data-testid="funds-phone-qr" className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-4">
      <div className="bg-[#0a0a0a] border border-white/15 rounded-2xl p-6 max-w-md w-full text-center">
        <div className="text-[11px] uppercase tracking-[0.25em] text-tv-accent font-bold inline-flex items-center gap-1.5 justify-center">
          <Smartphone className="w-3.5 h-3.5" /> Pay on your phone
        </div>
        <h3 className="font-display font-black text-2xl text-white mt-2">Scan to add ${Number(amount).toFixed(2)}</h3>
        <div className="bg-white p-3 rounded-xl inline-block mt-4">
          <img src={qrSrc} alt="QR code to Stripe Checkout" width={240} height={240} className="block" />
        </div>
        <p className="text-white/65 text-sm mt-4">
          Open your phone's camera and point it at the code. You'll be taken to Stripe Checkout — pay there, and your wallet will update automatically.
        </p>
        <div className="mt-5 flex flex-wrap justify-center gap-2">
          <a
            data-testid="funds-phone-qr-open"
            href={url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-bold px-4 py-2 rounded-full text-sm"
          >
            <ExternalLink className="w-3.5 h-3.5" /> Open here instead
          </a>
          <button
            data-testid="funds-phone-qr-close"
            onClick={onClose}
            className="inline-flex items-center gap-2 bg-tv-accent hover:bg-yellow-300 text-black font-display font-black px-4 py-2 rounded-full text-sm"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
